import { useEffect, useMemo, useState } from "react";
import { createNumberFormatter, type NumberFormatterLike } from "../lib/intl";
import { CalculatorIcon } from "./icons";

interface ConversionMatrixProps {
  btcPriceToman: number | null;
  usdtToman: number | null;
}

type UnitId = "btc" | "sat" | "toman" | "usd";

interface UnitDefinition {
  id: UnitId;
  englishLabel: string;
  persianLabel: string;
  ratioToBtc: number | null;
  formatter: NumberFormatterLike;
  maxFractionDigits: number;
  formatDisplay?: (raw: string) => string;
}

const tomanFormatter = createNumberFormatter("fa-IR", { maximumFractionDigits: 0 });
const btcFormatter = createNumberFormatter("fa-IR", { maximumFractionDigits: 8 });
const satFormatter = createNumberFormatter("fa-IR", { maximumFractionDigits: 0 });
const usdFormatter = createNumberFormatter("en-US", { maximumFractionDigits: 2 });
const inputIntegerFormatter = createNumberFormatter("fa-IR");

const COINGECKO_PRICE_URL = "https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd";
const GLOBAL_PRICE_TTL_MS = 5 * 60_000;

interface GlobalUsdPriceCache {
  value: number;
  fetchedAt: number;
}

let cachedGlobalUsdPrice: GlobalUsdPriceCache | null = null;
let globalUsdPricePromise: Promise<number | null> | null = null;

function getCachedGlobalUsdPrice(): number | null {
  if (!cachedGlobalUsdPrice) {
    return null;
  }
  const age = Date.now() - cachedGlobalUsdPrice.fetchedAt;
  if (age < GLOBAL_PRICE_TTL_MS && typeof cachedGlobalUsdPrice.value === "number" && Number.isFinite(cachedGlobalUsdPrice.value) && cachedGlobalUsdPrice.value > 0) {
    return cachedGlobalUsdPrice.value;
  }
  return null;
}

async function fetchGlobalUsdPrice(): Promise<number | null> {
  const cached = getCachedGlobalUsdPrice();
  if (cached !== null) {
    return cached;
  }
  if (globalUsdPricePromise) {
    return globalUsdPricePromise;
  }

  globalUsdPricePromise = (async () => {
    try {
      const res = await fetch(COINGECKO_PRICE_URL, {
        cache: "no-store",
      });
      if (!res.ok) {
        console.warn("[ConversionMatrix] CoinGecko price request failed", res.status, res.statusText);
        return null;
      }
      const json = (await res.json()) as { bitcoin?: { usd?: number } };
      const value = json?.bitcoin?.usd;
      if (typeof value === "number" && Number.isFinite(value) && value > 0) {
        cachedGlobalUsdPrice = { value, fetchedAt: Date.now() };
        return value;
      }
      return null;
    } catch (error) {
      console.warn("[ConversionMatrix] failed to load global BTC/USD price", error);
      return null;
    } finally {
      globalUsdPricePromise = null;
    }
  })();

  return globalUsdPricePromise;
}

const persianDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
const easternArabicDigits = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];

function normalizeNumericInput(value: string): string {
  if (!value) return "";
  const mapped = value
    .split("")
    .map((char) => {
      const persianIndex = persianDigits.indexOf(char);
      if (persianIndex >= 0) return String(persianIndex);
      const arabicIndex = easternArabicDigits.indexOf(char);
      if (arabicIndex >= 0) return String(arabicIndex);
      if (char === "٫" || char === "،") return ".";
      if (char === "٬" || char === "," || char === " ") return "";
      return char;
    })
    .join("");

  let dotSeen = false;
  let sanitized = "";
  for (const char of mapped) {
    if (char === ".") {
      if (dotSeen) {
        continue;
      }
      dotSeen = true;
      sanitized += char;
      continue;
    }
    if (/\d/.test(char)) {
      sanitized += char;
    }
  }
  return sanitized;
}

function formatInputDisplay(value: string): string {
  if (!value) return "";
  const endsWithDot = value.endsWith(".");
  const [integerPartRaw, decimalPartRaw = ""] = value.split(".");
  const integerNumber = Number(integerPartRaw || "0");
  const integerDisplay = integerPartRaw.length > 0 ? inputIntegerFormatter.format(integerNumber) : "۰";
  if (endsWithDot) {
    return `${integerDisplay}٫`;
  }
  if (decimalPartRaw.length === 0) {
    return integerDisplay;
  }
  const decimalDisplay = decimalPartRaw.replace(/\d/g, (digit) => persianDigits[Number(digit)]);
  return `${integerDisplay}٫${decimalDisplay}`;
}

function formatInputDisplayPersianWithDot(value: string): string {
  if (!value) return "";
  const endsWithDot = value.endsWith(".");
  const [integerPartRaw, decimalPartRaw = ""] = value.split(".");
  const integerNumber = Number(integerPartRaw || "0");
  const integerDisplay = integerPartRaw.length > 0
    ? inputIntegerFormatter.format(integerNumber)
    : "۰";
  if (endsWithDot) {
    return `${integerDisplay}.`;
  }
  if (decimalPartRaw.length === 0) {
    return integerDisplay;
  }
  const decimalDisplay = decimalPartRaw.replace(/\d/g, (digit) => persianDigits[Number(digit)]);
  return `${integerDisplay}.${decimalDisplay}`;
}

function numberToRawString(value: number, maxFractionDigits: number): string {
  return value.toLocaleString("en-US", {
    useGrouping: false,
    maximumFractionDigits: maxFractionDigits,
  });
}

type ComputedRow = UnitDefinition & {
  isFixed: boolean;
  computedValue: number | null;
  rawValue: string;
  displayValue: string;
  disabled: boolean;
};

export function ConversionMatrix({ btcPriceToman, usdtToman }: ConversionMatrixProps) {
  const [fixedUnit, setFixedUnit] = useState<UnitId>("btc");
  const [fixedRawValue, setFixedRawValue] = useState<string>("1");
  const [globalUsdPerBtc, setGlobalUsdPerBtc] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const publish = (value: number | null) => {
      if (!cancelled && typeof value === "number" && Number.isFinite(value) && value > 0) {
        setGlobalUsdPerBtc(value);
      }
    };

    const maybeCached = getCachedGlobalUsdPrice();
    if (maybeCached !== null) {
      publish(maybeCached);
    }

    const load = async () => {
      const value = await fetchGlobalUsdPrice();
      publish(value);
    };

    void load();
    const interval = window.setInterval(() => {
      void load();
    }, GLOBAL_PRICE_TTL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  const units = useMemo<UnitDefinition[]>(() => {
    const resolvedUsdPerBtc = (() => {
      if (typeof globalUsdPerBtc === "number" && Number.isFinite(globalUsdPerBtc) && globalUsdPerBtc > 0) {
        return globalUsdPerBtc;
      }
      if (btcPriceToman && usdtToman && btcPriceToman > 0 && usdtToman > 0) {
        return btcPriceToman / usdtToman;
      }
      return null;
    })();
    return [
      {
        id: "btc",
        englishLabel: "BTC",
        persianLabel: "بیت‌کوین",
        ratioToBtc: 1,
        formatter: btcFormatter,
        maxFractionDigits: 8,
      },
      {
        id: "sat",
        englishLabel: "SAT",
        persianLabel: "ساتوشی",
        ratioToBtc: 1 / 100_000_000,
        formatter: satFormatter,
        maxFractionDigits: 0,
      },
      {
        id: "toman",
        englishLabel: "IRT",
        persianLabel: "تومان",
        ratioToBtc: btcPriceToman ? 1 / btcPriceToman : null,
        formatter: tomanFormatter,
        maxFractionDigits: 0,
      },
      {
        id: "usd",
        englishLabel: "USDT",
        persianLabel: "تتر",
        ratioToBtc: resolvedUsdPerBtc ? 1 / resolvedUsdPerBtc : null,
        formatter: usdFormatter,
        maxFractionDigits: 2,
        formatDisplay: formatInputDisplayPersianWithDot,
      },
    ];
  }, [btcPriceToman, globalUsdPerBtc, usdtToman]);

  useEffect(() => {
    const base = units.find((unit) => unit.id === fixedUnit);
    if (base && base.ratioToBtc !== null) {
      return;
    }
    const fallback = units.find((unit) => unit.ratioToBtc !== null);
    if (fallback) {
      setFixedUnit(fallback.id);
      setFixedRawValue("1");
    }
  }, [fixedUnit, units]);

  const baseDefinition = units.find((unit) => unit.id === fixedUnit);
  const baseRatio = baseDefinition?.ratioToBtc ?? null;
  const baseNumeric = Number(fixedRawValue || "0");
  const baseBtc = baseRatio !== null ? baseNumeric * baseRatio : null;

  const rows = useMemo<ComputedRow[]>(() => {
    return units.map((unit) => {
      const disabled = unit.ratioToBtc === null;
      const isFixed = unit.id === fixedUnit;
      let rawValue = "";
      let computedValue: number | null = null;

      if (isFixed) {
        rawValue = fixedRawValue;
        computedValue = Number(fixedRawValue || "0");
      } else if (!disabled && baseBtc !== null && unit.ratioToBtc !== null) {
        computedValue = baseBtc / unit.ratioToBtc;
        rawValue = numberToRawString(computedValue, unit.maxFractionDigits);
      }

      const displayValue = rawValue
        ? (unit.formatDisplay ? unit.formatDisplay(rawValue) : formatInputDisplay(rawValue))
        : "";

      return {
        ...unit,
        isFixed,
        computedValue,
        rawValue,
        displayValue,
        disabled,
      };
    });
  }, [units, fixedUnit, fixedRawValue, baseBtc]);

  const handleFocus = (row: ComputedRow) => {
    if (row.disabled || row.isFixed) {
      return;
    }
    setFixedUnit(row.id);
    setFixedRawValue(row.rawValue ? normalizeNumericInput(row.rawValue) : "");
  };

  const handleChange = (row: ComputedRow, raw: string) => {
    if (row.disabled) {
      return;
    }
    const normalized = normalizeNumericInput(raw);
    setFixedUnit(row.id);
    setFixedRawValue(normalized);
  };

  return (
    <section className="rounded-2xl border border-slate-800/70 bg-slate-900/50 p-6 shadow-lg shadow-slate-950/30">
      <div className="space-y-4">
        <div className="text-right">
          <h3 className="flex flex-row-reverse items-center justify-end gap-2 text-lg font-semibold text-slate-100">
            <span>تبدیل سریع واحدها</span>
            <CalculatorIcon className="h-5 w-5 text-sky-300" aria-hidden />
          </h3>
          <p className="mt-2 text-sm text-slate-300">روی فیلد واحدی که می‌خواهید مبنا باشد کلیک کنید؛ مقدار جدید را وارد کنید تا بقیه ردیف‌ها خودکار به‌روزرسانی شوند.</p>
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-slate-800/60">
        <table className="min-w-full divide-y divide-slate-800/60 text-sm text-slate-100" role="grid">
          <tbody className="divide-y divide-slate-800/60">
            {rows.map((row) => {
              const englishId = `conversion-${row.id}-en`;
              const persianId = `conversion-${row.id}-fa`;
              const inputId = `conversion-${row.id}-input`;
              return (
                <tr key={row.id} className={row.isFixed ? "bg-slate-800/40" : undefined}>
                  <th scope="row" id={englishId} className="px-4 py-3 font-medium text-slate-50" dir="ltr">
                    {row.englishLabel}
                  </th>
                  <td className="px-4 py-3">
                    <label htmlFor={inputId} className="sr-only">
                      {`مقدار ${row.englishLabel} (${row.persianLabel})`}
                    </label>
                    <input
                      id={inputId}
                      type="text"
                      inputMode="decimal"
                      dir="ltr"
                      autoComplete="off"
                      value={row.displayValue}
                      onFocus={() => handleFocus(row)}
                      onChange={(event) => handleChange(row, event.target.value)}
                      disabled={row.disabled}
                      placeholder={row.disabled ? "—" : ""}
                      aria-labelledby={`${englishId} ${persianId}`}
                      aria-describedby={row.disabled ? `${inputId}-note` : undefined}
                      aria-disabled={row.disabled || undefined}
                      className={`w-full rounded-xl border px-3 py-2 text-right text-sm text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${
                        row.isFixed
                          ? "border-sky-500/70 bg-sky-500/10 text-sky-100"
                          : "border-slate-600 bg-slate-900/70"
                      } ${row.disabled ? "cursor-not-allowed opacity-60" : ""}`}
                    />
                    {row.disabled ? (
                      <p id={`${inputId}-note`} className="mt-1 text-xs text-slate-300">
                        قیمت مرجع برای این واحد در دسترس نیست
                      </p>
                    ) : null}
                  </td>
                  <td id={persianId} className="px-4 py-3 text-slate-50">
                    {row.persianLabel}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
